import { useState, useEffect } from 'react';
import SiteFooter from '../components/SiteFooter';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { Loader2 } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const fallbackSections = [
  {
    title: '1. 我們收集的個人資料',
    content: [
      '身份及聯絡資料：姓名、電話號碼、WhatsApp 號碼、電郵地址（如提供）。',
      '派送資料：詳細地址、屋苑名稱、地區。',
      '訂單資料：訂購產品、訂單金額、派送日期時間、訂單備註、訂單類型（送貨或自取）。',
      '付款資料：付款方式及閣下上傳的付款憑證（截圖或相片）。我們不會儲存閣下的銀行卡號碼或完整銀行賬戶資料。',
      '溝通記錄：閣下與我們之間的 WhatsApp 對話內容、客戶服務查詢及投訴記錄。',
      '推薦關係資料：閣下的推薦碼、透過推薦連結下單的記錄、推薦人與被推薦人的關聯，以及相關獎賞結算資料。',
      '技術資料：IP 地址、瀏覽器類型、裝置資料、瀏覽頁面及使用時間（透過 Cookie 及分析工具收集）。',
    ],
  },
  {
    title: '2. 收集資料的目的',
    content: [
      '訂單處理：處理及確認訂單、安排製作及派送、處理退款及售後事宜。',
      '派送安排：向負責閣下屋苑的派送方提供完成派送所需的資料。',
      '客戶服務：回覆查詢、處理投訴、通知訂單狀態（包括透過 WhatsApp 發送訂單確認、付款確認、派送通知等訊息）。',
      '推薦計劃營運：核實推薦關係、計算及發放推薦獎賞、防止推薦系統被濫用。',
      '直接促銷：在取得閣下同意或不反對的情況下，透過 WhatsApp 或其他渠道向閣下發送新產品、優惠及活動資訊。',
      '系統改進：分析平台使用情況、改善產品及服務、進行統計及業務分析。',
      '法律合規：遵守適用法律、法庭命令或監管要求，以及保障我們的合法權益。',
    ],
  },
  {
    title: '3. 資料的披露及轉移',
    content: [
      '我們不會出售閣下的個人資料。我們僅會在以下情況披露或轉移閣下的資料：',
      '好餸員工及承辦人：因履行職務需要而接觸資料的員工及受保密義務約束的服務承辦人。',
      '派送合作夥伴：負責閣下屋苑派送的服務承辦人，惟僅限完成派送所需的資料。',
      '支付及金融機構：處理閣下付款的銀行及轉數快系統營運商（僅限完成交易所需）。',
      '雲端服務供應商：Cloudflare, Inc.（提供 D1 數據庫及 R2 儲存服務），作為資料處理者代我們儲存資料。',
      '通訊平台：WhatsApp 及 Meta Platforms, Inc.（透過 WhatsApp Business API 向閣下發送訊息，閣下的電話號碼及訊息內容會按 Meta 的私隱政策處理）。',
      '法律要求：按法律、法庭命令、執法機關或監管機構的合法要求作出披露。',
      '權益保障：為保障我們、客戶或公眾的權利、財產或安全而必需披露的情況。',
      '如上述資料被轉移至香港以外的伺服器，我們將採取合理措施確保資料獲得與私隱條例標準相稱的保護。',
    ],
  },
  {
    title: '4. 派送資料保護機制',
    content: [
      '為完成派送服務，我們會向派送合作夥伴提供必要的訂單資料。我們實施以下資料保護措施：',
      '客戶姓名：部分遮罩處理，不會完整顯示。',
      '客戶電話：遮罩處理，僅顯示部分數字。',
      '派送地址：為完成派送所必需，故會完整顯示。',
      '訂單內容及金額：顯示作核對派送貨品之用。',
      '派送合作夥伴受合約保密義務約束，僅可為派送目的使用上述資料，不得記錄、複製、轉售或向任何第三方披露。違反者將承擔法律責任。',
      '如客戶對派送安排有特別私隱考慮，可於落單前透過 WhatsApp 聯絡我們，我們將在可行範圍內另行安排。',
    ],
  },
  {
    title: '5. 資料儲存與保安',
    content: [
      '閣下的個人資料儲存於 Cloudflare 提供的 D1 數據庫及 R2 物件儲存服務。',
      '我們採取以下保安措施：所有平台與伺服器之間的資料傳輸均以 HTTPS/TLS 加密；後台系統實施身份驗證及權限控制，僅授權人員可存取個人資料；付款憑證圖片設有存取限制，僅供核對款項之用；定期檢視系統保安設定及存取記錄。',
      '互聯網傳輸不能保證絕對安全。我們會盡商業上合理的努力保護閣下的資料，惟無法保證完全免受未經授權的存取。如發生資料安全事故，我們將按適用法律及私隱專員公署的指引處理及通報。',
    ],
  },
  {
    title: '6. 資料保留期限',
    content: [
      '訂單及客戶資料：訂單完成後保留 7 年（符合香港稅務及會計紀錄要求），其後刪除或匿名化。',
      '付款憑證：核實款項後保留 12 個月，其後刪除。',
      'WhatsApp 對話記錄：保留 24 個月，用作客戶服務及爭議處理。',
      '推薦關係及獎賞記錄：於推薦計劃相關賬目結算完成後保留 7 年。',
      '營銷通訊名單：直至閣下撤回同意或選擇拒收為止。',
      '保留期屆滿後，我們將以不可還原的方式刪除或匿名化有關資料。',
    ],
  },
  {
    title: '7. Cookie 與分析工具',
    content: [
      '我們的平台使用 Cookie 及類似技術，用於維持購物車及訂單流程運作（必要 Cookie）、記住閣下的偏好設定、分析網站流量及使用模式，以改善平台表現。',
      '閣下可透過瀏覽器設定拒絕或刪除 Cookie，惟可能影響部分平台功能（包括落單流程）的正常運作。',
    ],
  },
  {
    title: '8. 閣下的權利',
    content: [
      '根據私隱條例，閣下有權：查閱我們持有閣下的個人資料；更正不準確的個人資料；在法律容許的範圍內，要求刪除閣下的個人資料；反對直接促銷。',
      '直接促銷聲明：我們擬使用閣下的姓名及電話號碼，透過 WhatsApp 向閣下發送產品優惠、活動資訊及社企動態。未經閣下同意（或閣下表示不反對），我們不會如此使用閣下的資料。閣下可隨時透過訊息中的拒收指示或聯絡我們撤回同意，費用全免。',
      '如欲行使上述權利，請聯絡我們。我們將於收到要求後 40 日內回覆。就查閱資料要求，我們可按私隱條例收取合理行政費用。',
    ],
  },
  {
    title: '9. 未成年人保護',
    content: [
      '我們的服務主要對象為 18 歲或以上人士。如閣下未滿 18 歲，請在家長或監護人陪同及同意下使用本服務及提供個人資料。',
      '我們不會明知而故意收集未成年人的個人資料。如發現誤收有關資料，我們將盡快刪除。',
    ],
  },
  {
    title: '10. 私隱政策的修改',
    content: [
      '我們可不時更新本政策。更新版本將刊載於本網站並註明「最後更新日期」。重大變更將透過 WhatsApp 或網站公告另行通知。閣下於更新後繼續使用服務，即視為接受更新後的政策。',
    ],
  },
  {
    title: '11. 聯絡方法（私隱查詢）',
    content: [
      '如對本政策或我們處理個人資料的方式有任何查詢、投訴，或欲行使上述權利，請聯絡好餸社企個人資料查詢：WhatsApp（透過網站所列的客戶服務號碼）或瀏覽 shop.deliciousexpress.com.hk。',
      '閣下亦有權向香港個人資料私隱專員公署（www.pcpd.org.hk）作出投訴。',
    ],
  },
];

function parseSections(rawText: string) {
  if (!rawText.trim()) return null;
  const lines = rawText.split('\n').filter(l => l.trim());
  const sections: { title: string; content: string[] }[] = [];
  let current: { title: string; content: string[] } | null = null;
  for (const line of lines) {
    const trimmed = line.trim();
    if (/^\d+\./.test(trimmed) || /^第.*/.test(trimmed) || trimmed.endsWith('：') || trimmed.endsWith(':')) {
      if (current) sections.push(current);
      current = { title: trimmed, content: [] };
    } else if (current) {
      current.content.push(trimmed);
    }
  }
  if (current) sections.push(current);
  return sections.length > 0 ? sections : null;
}

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const [sections, setSections] = useState(fallbackSections);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/public/settings/terms')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.privacy_policy) {
          const parsed = parseSections(data.privacy_policy);
          if (parsed) setSections(parsed);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // EN 模式顯示 en/content.json 的法律正文鏡像；缺鏡像時回退 API/fallback 內容（zh 恆用 API/fallback）
  const isEn = i18n.language === 'en';
  const mirror = isEn
    ? t('content:legal.privacy.sections', { returnObjects: true }) as unknown
    : null;
  const displaySections =
    mirror && Array.isArray(mirror) && mirror.length > 0
      ? (mirror as { title: string; content: string[] }[])
      : sections;

  return (
    <>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8 transition-colors">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 md:p-10 transition-colors">
          {/* 本頁無 SiteHeader，自帶語言切換器 */}
          <div className="flex justify-end mb-2">
            <LanguageSwitcher size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('content:legal.heading1')}
          </h1>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
            {t('content:legal.privacy.heading2')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            {t('content:legal.privacy.lastUpdated')}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-orange-600" size={32} />
            </div>
          ) : (
            <div className="space-y-8">
              {displaySections.map((section) => (
                <section key={section.title}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {section.title}
                  </h3>
                  <div className="space-y-3 text-gray-700 dark:text-gray-300 leading-relaxed">
                    {section.content.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/"
              className="inline-flex items-center text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
            >
              {t('content:legal.backHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
    <SiteFooter />
    </>
  );
}
